import { useEffect, useRef, useState } from "react"

import { Badge, Button, Card } from "@coval/ui"
import { Library } from "lucide-react"

import type { PersonaCatalogItemResource } from "@/types_api/backend/transport/list_persona_catalog_entries_response"

interface AddPersonaCardProps {
  isAdded: boolean
  addLabel?: string
  item: PersonaCatalogItemResource
  onAdd: (entryId: string) => Promise<void> | void
}

export function AddPersonaCard({
  isAdded,
  item,
  onAdd,
  addLabel = "Add",
}: AddPersonaCardProps) {
  const [isSaving, setIsSaving] = useState(false)
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleAdd = async () => {
    setIsSaving(true)
    try {
      await onAdd(item.entry_id)
    } finally {
      if (mountedRef.current) {
        setIsSaving(false)
      }
    }
  }

  return (
    <Card
      className="flex items-start gap-3 rounded-md p-3 transition-colors duration-150 hover:bg-surface-default-hover"
      data-testid={`add-persona-card-${item.entry_id}`}
    >
      <Library className="mt-0.5 size-4 shrink-0 text-coval-text-muted" />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-coval-text">
            {item.display_name}
          </span>
          {item.status === "inactive" && (
            <Badge
              variant="warning"
              className="h-5 shrink-0 px-1.5 text-[10px]"
            >
              INACTIVE
            </Badge>
          )}
        </div>

        {item.description && (
          <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-coval-text-muted">
            {item.description}
          </p>
        )}

        {item.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {item.tags.map((tag) => (
              <Badge
                key={tag}
                variant="outline"
                className="h-5 px-1.5 text-[10px]"
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <Button
        variant="secondary"
        className="h-7 shrink-0 px-2.5 text-xs"
        loading={isSaving}
        disabled={isAdded}
        onClick={() => void handleAdd()}
      >
        {isSaving ? "Adding" : isAdded ? "Added" : addLabel}
      </Button>
    </Card>
  )
}
